import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { LoadingService } from "@coso/loading";
import * as lodash from "lodash";
import { Observable, zip } from "rxjs";
import { filter } from "rxjs/operators";
import { IConfigurationHolder, IEventPriorityHolder, ItemPriority, ItemPriorityName, MessagesName } from "../../../models/auto-generated";
import { EventsService } from "../../../services/events.service";
import { SettingsStore } from "../../../stores/settings.store";
import { handleError } from "../../../utils/handle-error";

@Component({
  standalone:false,
  templateUrl: "./event-priority.component.html",
  styleUrls: ["./event-priority.component.scss"]
})
export class EventPriorityComponent implements OnInit {
  ItemPriority = ItemPriority;
  itemPriorities = Object.keys(ItemPriority).filter((value) => isNaN(Number(value)));
  errorMessage: string;
  events: IEventPriorityHolder[] = [];
  settings$: Observable<IConfigurationHolder>;
  readonly$: Observable<boolean>;
  instanceId: string;
  private original: IEventPriorityHolder[];

  constructor(public loadingService: LoadingService, private settingsStore: SettingsStore, private eventsService: EventsService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.settings$ = this.settingsStore.settings$;
    this.readonly$ = this.settingsStore.isReadOnly$;
    this.settingsStore.settingsReset$.subscribe(() => (this.events = lodash.cloneDeep(this.original)));
    this.instanceId = this.route.parent.snapshot.paramMap.get("instance-id");
    this.loadEventPrioritySettings();
  }

  getItemPriorityName(priority: string): string {
    return ItemPriorityName.get(ItemPriority[priority]);
  }

  getEventName(event: IEventPriorityHolder) {
    return MessagesName.get(event.name);
  }

  eventPriorityChanged(current: IConfigurationHolder, event: IEventPriorityHolder) {
    const eventPriorityIndex = current.eventPriorityConfiguration.findIndex((element) => element.name === event.name);
    const hasDefaultValue = this.getPriorityDefaultValue(event.name) === event.priority;
    if (hasDefaultValue) {
      current.eventPriorityConfiguration.splice(eventPriorityIndex, 1);
    } else if (eventPriorityIndex > -1) {
      current.eventPriorityConfiguration[eventPriorityIndex].priority = event.priority;
    } else {
      current.eventPriorityConfiguration.push({
        name: event.name,
        priority: event.priority
      });
    }
    this.settingsStore.updateSettings(current);
  }

  private loadEventPrioritySettings() {
    this.loadingService.start("Loading event priority settings", "loadEventPriority");
    zip(this.settingsStore.settings$.pipe(filter((s) => !!s)), this.eventsService.get(this.instanceId)).subscribe(
      ([settings, events]) => {
        for (const event of events) {
          let priority: ItemPriority;
          const eventPriority = settings.eventPriorityConfiguration.find((e) => e.name === event.value);
          if (eventPriority) {
            priority = eventPriority.priority;
          } else {
            priority = this.getPriorityDefaultValue(event.value);
          }
          this.events.push({
            name: event.value,
            priority: priority
          });
        }
        this.original = lodash.cloneDeep(this.events);
        this.loadingService.stop("loadEventPriority");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadEventPriority");
      }
    );
  }

  private getPriorityDefaultValue(value: number): ItemPriority {
    switch (value) {
      case 501:
      case 502:
      case 9001:
      case 9002:    
        return ItemPriority.Highest;
      case 1001:
      case 1002:
      case 1003:
      case 131:
      case 132:
        return ItemPriority.AboveNormal;
      default:
        return ItemPriority.Normal;
    }
  }
}
